import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  input,
  LOCALE_ID,
  output,
  signal,
  effect,
} from '@angular/core';
import { DatePipe } from '@angular/common';

import { toRouteInstanceTableRows } from './route-instance-table.mapper';
import { ActionInterface } from '../../../../../../pages/admin-dashboard/pharmacies/pharmacies-list/pharmacies-list.model';
import { Loading } from '../../../../../../utils/loading/loading';
import { EmptyData } from '../../../../../../utils/empty-data/empty-data';
import { ErrorData } from '../../../../../../utils/error-data/error-data';
import { Tables } from '../../../../../../shared/ui/shared-tables/tables/tables';
import { Pagination } from '../../../../../../shared/ui/shared-tables/pagination/pagination';
import { RouteInstanceItem } from '../../../../../../pages/admin-dashboard/admin-route-instance-management/route-instances.model';
import { ConfirmationDialog } from '../../../../../../shared/ui/confirmation-dialog/confirmation-dialog';

export type LoadState = 'loading' | 'success' | 'error';

@Component({
  selector: 'app-route-instances-table',
  standalone: true,
  imports: [DatePipe, Loading, EmptyData, ErrorData, Tables, Pagination, ConfirmationDialog],
  templateUrl: './route-instances-table.html',
  styleUrl: './route-instances-table.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RouteInstancesTable {
  private cdr = inject(ChangeDetectorRef);
  private locale = inject(LOCALE_ID);

  showCancelDialog = signal(false);
  instances = input<RouteInstanceItem[] | null>(null);
  state = input<LoadState>('loading');
  currentPage = input<number>(1);
  itemsPerPage = input<number>(25);
  totalItems = input<number>(0);
  totalPages = input<number>(1);
  isFiltersApplied = input<boolean>(false);
  startRouteInstanceError = input<string>('');
  cancelRouteError = input<string>('');

  pageChange = output<number>();
  pageSizeChange = output<number>();
  cancelRouteInstance = output<{ id: number; comment: string }>();
  rowClick = output<number>();
  showStartDialog = signal(false);
  selectedInstanceId = signal<number | null>(null);
  closeStartDialog = input<number>(0);
  startRouteInstance = output<{ id: number; comment: string }>();
  rows = computed(() => toRouteInstanceTableRows(this.instances() ?? [], this.isArabic()));
  startRouteInstanceSuccess = output<void>();
  activeDropdownId = signal<number | null>(null);
  currentActions = signal<ActionInterface[]>([]);

  constructor() {
    effect(() => {
      if (this.closeStartDialog() > 0) {
        this.showStartDialog.set(false);
        this.selectedInstanceId.set(null);
      }
      const instances = this.instances();
      const activeId = this.activeDropdownId();

      if (!instances || activeId === null) return;

      const instance = instances.find((r) => r.id === activeId);

      if (instance) {
        this.currentActions.set(this.getActions(instance));
      }
    });
  }

  tableHeaderData = signal([
    { key: 'index', label: $localize`:@@routeInstanceTableIndex:#` },
    { key: 'routeName', label: $localize`:@@routeInstanceTableName:Route Name` },
    { key: 'ordersCount', label: $localize`:@@routeInstanceTableOrders:Orders` },
    { key: 'pharmaciesCount', label: $localize`:@@routeInstanceTablePharmacies:Pharmacies` },
    { key: 'distributor', label: $localize`:@@routeInstanceTableDistributor:Distributor` },
    { key: 'createdOnBy', label: $localize`:@@routeInstanceTableCreatedOn:Created On / By` },
    { key: 'lastModifiedOnBy', label: $localize`:@@routeInstanceTableLastModified:Last Modified On / By` },
    { key: 'dispatchTime', label: $localize`:@@routeInstanceTableIntervals:Intervals` },
    { key: 'status', label: $localize`:@@routeInstanceTableStatus:Status`, type: 'status' },
    { key: 'actions', label: '', type: 'actions' },
  ]);

  startItem = computed(() => {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.itemsPerPage() + 1;
  });

  endItem = computed(() => {
    const end = this.currentPage() * this.itemsPerPage();
    return end > this.totalItems() ? this.totalItems() : end;
  });

  pageNumbers = computed(() => {
    const total = this.totalPages();
    const current = this.currentPage();
    const delta = 2;
    const range: number[] = [1];
    if (total <= 1) return range;
    for (let i = current - delta; i <= current + delta; i++) {
      if (i > 1 && i < total) range.push(i);
    }
    range.push(total);
    const result: number[] = [];
    let prev: number | undefined;
    for (const i of range) {
      if (prev !== undefined) {
        if (i - prev === 2) result.push(prev + 1);
        else if (i - prev !== 1) result.push(-1);
      }
      result.push(i);
      prev = i;
    }
    return result;
  });

  isArabic() {
    return this.locale.startsWith('ar');
  }

  getActions(instance: RouteInstanceItem): ActionInterface[] {
    const actions: ActionInterface[] = [
      { id: 1, titleEn: 'View Details', titleAr: 'عرض التفاصيل' },
      instance.distributor
        ? { id: 2, titleEn: 'Reassign Distributor', titleAr: 'إعادة تعيين موزع' }
        : { id: 3, titleEn: 'Assign Distributor', titleAr: 'تعيين موزع' },
    ];

    if (instance.status?.id === 510502) {
      actions.push({
        id: 5,
        titleEn: 'Start Route Instance',
        titleAr: 'بدء وردية خط السير',
      });
    }

    if (instance.status?.id === 510501) {
      actions.push({
        id: 4,
        titleEn: 'Cancel Route Instance',
        titleAr: 'إلغاء وردية خط السير',
      });
    }

    return actions;
  }

  toggleDropdown(id?: any) {
    if (this.activeDropdownId() === id) {
      this.activeDropdownId.set(null);
    } else {
      this.activeDropdownId.set(id);
      const instance = this.instances()?.find((r) => r.id === id);
      if (instance) this.currentActions.set(this.getActions(instance));
    }
  }

  handleAction(e: { event: Event; actionId: number; entity: any }) {
    const { event, actionId, entity } = e;
    event?.stopPropagation();
    switch (actionId) {
      case 1:
        this.rowClick.emit(entity.id);
        break;
      case 5:
        this.selectedInstanceId.set(entity.id);
        this.showStartDialog.set(true);
        break;
      case 4:
        this.selectedInstanceId.set(entity.id);
        this.showCancelDialog.set(true);
        this.cdr.detectChanges();
        break;
    }
  }

  onStartSubmit(comment: string) {
    this.startRouteInstance.emit({
      id: this.selectedInstanceId()!,
      comment,
    });
  }

  onCancelSubmit(comment: string) {
    this.cancelRouteInstance.emit({
      id: this.selectedInstanceId()!,
      comment,
    });
    this.showCancelDialog.set(false);
  }

  onPreviousPage() {
    this.pageChange.emit(this.currentPage() - 1);
  }

  onNextPage() {
    this.pageChange.emit(this.currentPage() + 1);
  }

  onGoToPage(page: number) {
    this.pageChange.emit(page);
  }

  onChangePageNumber(size: number) {
    this.pageSizeChange.emit(Number(size));
  }

  onPageSizeChange() {}
}
